/* eslint-disable custom-rules/modal-and-drawer-requires-title */
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { RecentActivityDrawer } from '../../common/RecentActivity/RecentActivityDrawer';
import { CountySearchContext } from '../../context/CountySearchContext';
import { PortalContext } from '../../context/PortalContext';
import { SearchLocation } from '../../frontends/ProviderSearch/context/SearchLocation';
import {
  ButtonStyles,
  IconStyles,
} from '../../frontends/ProviderSearch/routes/NullSpecialtyPage/NullSpecialtyPage.styles';
import { useDeviceLocation } from '../../hooks/useDeviceLocation';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { useGetRecentSearchesResults } from '../../hooks/useRecentSearches';
import { useGuestStore } from '../../store/useGuestStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { getCurrentMember, parseLOB } from '../../utils/user.utils';
import { CardHeader } from '../CardHeader/CardHeader';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { cardHeaderNullPageStyle, phoneOnly } from '../ConstantsStyles';
import { LinkWithTracking } from '../LinkWithTracking/LinkWithTracking';
import { LocationSearchMobile } from '../PSXHeader/LocationSearchMobile/LocationSearchMobile';
import {
  getGeoLocationFromStorage,
  setGeoLocationToStorage,
} from '../PSXHeader/SearchBar/utils';
import { isCounty } from '../SnackCardContainer/utils';
import { getLagoonValueByKey } from '../Utils/getLagoonValueByKey';
import {
  NullResultSectionStyled,
  StyledUl,
  SubHeader,
  Wrapper,
} from './NullResultsPage.styles';

export type NullPageProps = {
  searchTitle: string;
  lagoonData: any;
  specialtySearch?: boolean;
};

const parseLagoonContent = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const liElements = doc.querySelectorAll('li');
  const processedContent = Array.from(liElements).map((li) => {
    const span = li.querySelector('span');
    const placeholder = `{${span?.id}}`;
    span?.replaceWith(placeholder);
    return {
      text: li.textContent ? li.textContent.trim() : '',
      id: span?.id || null,
      buttontext: span?.textContent || null,
    };
  });
  return processedContent;
};

export const NoResult = ({
  searchTitle,
  lagoonData,
  specialtySearch,
}: NullPageProps) => {
  const { navigate } = useRouter();
  const { t } = useTranslation();
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  const currentMember = getCurrentMember();
  const lob = currentMember?.lineOfBusiness;
  const parsedLob = parseLOB(lob);
  const { portalData } = useContext(PortalContext);
  const isGuestUser = portalData?.portalConfig?.isGuestUser;
  const [isOpenLocationChange, setIsOpenLocationChange] = useState(false);
  const [, setDeviceLocation] = useState<string>(
    getGeoLocationFromStorage() ?? ''
  );
  const { setIsCountySearch } = useContext(CountySearchContext);

  const mobileScreen = useMediaQuery(phoneOnly);
  const headingKey = Constants.RECENT_ACTIVITY_HEADING_KEY['All'];
  const headingsLagoon = useLagoon('recent-searches')();
  const allCareDrawerHeading = getLagoonValueByKey(
    headingsLagoon,
    headingKey,
    'drawer'
  );
  const [isOpen, setIsOpen] = useState(false);

  const { data } = useGetRecentSearchesResults();

  const handleChangeLocation = () => {
    setIsOpenLocationChange(true);
  };
  const handleOnClickHistory = () => {
    setIsOpen(true);
  };
  const handleOnClickViewPlanOptions = () => {
    // Dispatch custom event for parent application to handle navigation
    const navigationEvent = new CustomEvent('guestNavigationRequest', {
      detail: {
        path: '/select-coverage-type',
        params: { planSelectionLob: parsedLob },
      },
      bubbles: true,
    });
    window.dispatchEvent(navigationEvent);
  };

  const handleBack = () => {
    setTypeaheadSearchStore({
      searchType: Constants.SEARCH_TYPES.NAME,
      previous: true,
    });
    navigate(-1);
  };

  const handleOnClose = () => {
    setIsOpenLocationChange(false);
  };

  const { getDeviceLocation } = useDeviceLocation(
    setDeviceLocation,
    setIsOpenLocationChange
  );
  const geoLocation = getGeoLocationFromStorage();

  /* The current Lagoon content is in markdown format, which requires
  using createPortal to inject a React component (Link) into the span element.
   This approach causes issues as it uses both React and plain JavaScript
   to manage the DOM, leading to errors when the user is on the NSR page and
   searches for a term that has results with a coverage type different from
   the one on the NSR page. To address this, we are using the parsed Lagoon
   content to render the content as a list of items with text and IDs.
   This approach allows us to render the content without relying on createPortal.
*/
  const hasGuestCoverageType = useGuestStore((state: any) =>
    Boolean(state?.guestPlanSelectorData?.coverageType)
  );
  const isGuestDeeplinkFlow = useGuestStore((state: any) =>
    Boolean(state?.guestPlanSelectorData?.isGuestDeeplinkFlow)
  );
  const [enableNsrSuppress] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NSR_SUPRESS,
  ]);

  const shouldHideCoverageOption =
    enableNsrSuppress && hasGuestCoverageType && isGuestDeeplinkFlow;
  const parsedLagoonContent = lagoonData?.content
    ? parseLagoonContent(lagoonData.content)
    : [];

  const visibleLagoonContent = shouldHideCoverageOption
    ? parsedLagoonContent.filter((item) => item.id !== 'view-plan-options')
    : parsedLagoonContent;

  const getLinkDataTestId = (id: string) => {
    switch (id) {
      case 'nullpage-history':
        return {
          testId: 'null-page-view-history',
          onClick: handleOnClickHistory,
        };
      case 'view-plan-options':
        return {
          testId: 'null-page-view-plan-options',
          onClick: handleOnClickViewPlanOptions,
        };
      case 'nullpage-location':
        return { testId: 'null-page-location', onClick: handleChangeLocation };
      default:
        return { testId: id, onClick: () => {} };
    }
  };

  const { name: locationName } = geoLocation;
  if (!lagoonData) return null;
  return (
    <Fragment>
      <NullResultSectionStyled>
        <Wrapper data-auto-testid="null-page" data-testid="null-page">
          <CardHeader
            css={cardHeaderNullPageStyle}
            data-auto-testid="browse-common-specialities-heading"
            data-testid="browse-common-specialities-heading"
            level={4}
          >
            {lagoonData?.heading} {`'${searchTitle}'`}
          </CardHeader>
          <SubHeader
            color="$core.color.neutral.80"
            css={{
              '&.abyss-heading-root': {
                fontWeight: '600',
                fontSize: '14.22px',
                lineHeight: '16px',
              },
            }}
            data-auto-testid="browse-common-specialities-sub-heading"
            data-testid="browse-common-specialities-sub-heading"
            level={6}
          >
            {lagoonData.subHeading}
          </SubHeader>
          <StyledUl
            data-auto-testid="null-search-suggestions"
            data-testid="null-search-suggestions"
          >
            {visibleLagoonContent?.map((item, index) => (
              <li key={index}>
                {!item?.id ? (
                  item?.text
                ) : (
                  <span>
                    {item?.text.split(`{${item?.id}}`)[0]}
                    <Text fontSize="sm">
                      <LinkWithTracking
                        css={{
                          lineHeight: '15px',
                        }}
                        data-auto-testid={`${
                          getLinkDataTestId(item?.id).testId
                        }`}
                        data-testid={`${getLinkDataTestId(item?.id).testId}`}
                        isStandardAnchor
                        onClick={getLinkDataTestId(item?.id).onClick}
                        size="$md"
                      >
                        {item?.buttontext}
                      </LinkWithTracking>
                    </Text>
                    {item?.text.split(`{${item?.id}}`)?.[1]}
                  </span>
                )}
              </li>
            ))}
          </StyledUl>
          {specialtySearch && !isGuestUser ? (
            <Button
              css={ButtonStyles}
              data-auto-testid="back-to-previous-page"
              data-testid="back-to-previous-page"
              onClick={handleBack}
              size="$md"
            >
              {t('NULL_PAGE.NULL_RESULTS_BACK_TO_PREVIOUS_PAGE_BUTTON')}
              <IconSymbol
                color="white"
                css={IconStyles}
                icon="chevron_right"
                isScreenReadable
                size="24px"
              />
            </Button>
          ) : null}
        </Wrapper>
      </NullResultSectionStyled>
      {mobileScreen && isOpenLocationChange ? (
        <Drawer
          css={{
            'abyss-modal-close-button': {
              marginTop: '$md',
              marginRight: '8px',
            },
            'abyss-modal-header-container': {
              marginLeft: '$sm',
            },
            'abyss-modal-base-content-container': {
              borderRadius: '20px 20px 0px 0px',
            },
          }}
          header={
            <Heading color="$core.color.neutral.90" level={5} size="xs">
              {!locationName ? t('My search location') : t('Location Change')}
            </Heading>
          }
          isOpen={isOpenLocationChange}
          onClose={() => setIsOpenLocationChange(false)}
          position="right"
          size="$lg"
        >
          <LocationSearchMobile
            findUserLocation={getDeviceLocation}
            location={locationName}
            setIsOpen={setIsOpenLocationChange}
            setLocation={(targetLocation) => {
              const {
                id,
                center = [],
                place_name: placeName,
                stateCode,
                zipCode,
              } = targetLocation;
              const [selectedLong, selectedLat] = center;
              if (isCounty(id)) setIsCountySearch(true);
              else setIsCountySearch(false);

              setGeoLocationToStorage({
                id,
                name: placeName,
                longitude: selectedLong,
                latitude: selectedLat,
                stateCode,
                zipCode,
              });
            }}
          />
        </Drawer>
      ) : null}
      {!mobileScreen && isOpenLocationChange ? (
        <ModalDialog
          header=""
          isOpen={isOpenLocationChange}
          onClose={handleOnClose}
          size="fullscreen"
        >
          <SearchLocation
            handleOnClose={handleOnClose}
            isOpenLocationChange={isOpenLocationChange}
          />
        </ModalDialog>
      ) : null}
      {data?.recentSearches ? (
        <div data-testid="recent-search">
          <RecentActivityDrawer
            drawerHeading={allCareDrawerHeading}
            isOpen={isOpen}
            recentSearchesResponse={data?.recentSearches}
            setIsOpen={setIsOpen}
          />
        </div>
      ) : null}
    </Fragment>
  );
};
